/**
 * `client/src/audio/musique.ts` — le lecteur de musique, au-dessus de Web Audio.
 *
 * Il partage **le contexte audio et le gain maître de `SoundProvider`** : deux `AudioContext`
 * sur une même page, ce sont deux horloges qui dérivent l'une par rapport à l'autre, et un jeu
 * de rythme n'a pas les moyens de ça. `SoundProvider` reste donc le propriétaire du contexte ;
 * ce fichier ne fait que lui emprunter une sortie.
 *
 * ## Ce qu'il garantit, et qui n'est pas négociable
 *
 * 1. **`mute` coupe TOUT**, sans exception ni écran privilégié. ⟦A-71⟧ — SPEC-UI §7.6 disait
 *    l'inverse (« la musique du Montage n'est jamais coupée par `mute`, elle est le gameplay ») ;
 *    ⟦A-22⟧ ne le permet pas. Un joueur qui coupe le son ne doit pas jouer un autre jeu que les
 *    autres, et surtout pas un jeu plus dur.
 * 2. **Rien n'attend la musique.** `jouer()` est asynchrone et ne rend jamais d'erreur : fichier
 *    absent, décodage impossible, navigateur qui refuse — l'écran continue. Un appelant qui
 *    ferait `await jouer()` avant d'animer quoi que ce soit aurait déjà tout faux, et le type
 *    de retour (`void`) est là pour l'en empêcher.
 * 3. **`positionMs()` peut rendre `null`**, et c'est le cas NORMAL, pas le cas d'erreur : le son
 *    est coupé, ou pas encore décodé. Tout consommateur doit avoir une horloge à lui et se
 *    servir de la position uniquement pour *décorer*.
 *
 * ## Pourquoi la position ne se lit pas sur un compteur à soi
 *
 * `AudioContext.currentTime` est l'horloge de la carte son. Un `Date.now()` ou un
 * `performance.now()` dérive d'elle de quelques millisecondes par minute — invisible sur une
 * animation, fatal sur une grille de temps qu'on prétend suivre. On lit donc toujours l'heure
 * de celui qui joue le son.
 */

import { lireBeatmap, type Beatmap, type PisteDisponible } from "./beatmap";
import { mesurerLatenceSortie, type MesureLatence } from "./latence";

/** Une entrée du bloc `music` de `client/public/audio/manifest.json` (SPEC-UI §7.6). */
export interface EntreeMusique {
  /** Chemin du fichier, relatif à `public/`. */
  file: string;
  /** Chemin de la beatmap, relatif à `public/`. Absente = morceau sans grille. */
  beatmap?: string;
  /** BPM natif, recopié du manifeste pour que `choisirPiste` n'ait pas à charger les grilles. */
  bpm?: number;
}

export type ManifesteMusique = Record<string, EntreeMusique | null>;

export interface OptionsLecture {
  /** Rejoue en boucle. Une ambiance boucle ; une fanfare de cérémonie, non. */
  boucle?: boolean;
  /** Volume propre au morceau, multiplié par le volume général. */
  volume?: number;
  /** Étirement du tempo (voir `choisirPiste`). 1 = tempo natif. */
  playbackRate?: number;
  /** Fondu d'entrée, en ms. Évite le claquement d'un démarrage sec. */
  fondueMs?: number;
  /**
   * ⟦C50 · A-169 §2⟧ Cale une POSITION du fichier sur un instant de l'écran : la position `positionMs`
   * du fichier doit être **ENTENDUE** `dansMs` ms après l'appel (« entendue » : latence de sortie
   * comprise, voir `compenserLatence`).
   *
   * C'est le geste du Montage : l'horloge du mini-jeu se remet à zéro sur un geste du joueur, et chaque
   * pattern dit quel instant du morceau est son zéro (`origineMs`) : `jouer(id, { caler: { positionMs:
   * origineMs, dansMs: leadInMs } })` fait entendre l'origine pile à la fin du décompte, donc sur le zéro
   * de la piste ; chaque carré, posé sur une attaque du morceau, tombe sur elle. Le décompte se joue sur
   * la musique qui précède l'origine : le fichier démarre dans le morceau, à la position qui garde l'origine
   * juste (l'origine d'un pattern est toujours après son décompte).
   *
   * Si le décodage a pris plus de temps que le calage n'en laissait, la lecture démarre **plus loin** dans le
   * morceau, à la position qui garde l'origine juste : le calage prime sur l'intro. Sans position
   * demandée, le morceau part de son début à l'instant de l'appel.
   */
  caler?: { positionMs: number; dansMs: number };
  /**
   * ⟦C50⟧ Avance le départ de la latence de sortie mesurée (`latenceSortieMs`), pour que le son soit
   * ENTENDU à l'instant demandé et pas seulement rendu. Vrai par défaut : c'est le correctif de l'écart
   * « les carrés ne sont pas dans le rythme de la musique » (176 ms sur un casque Bluetooth). Ne joue que
   * avec `caler`.
   */
  compenserLatence?: boolean;
}

interface Reglages {
  mute: boolean;
  volume: number;
}

export class LecteurMusique {
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly grilles = new Map<string, Beatmap | null>();
  private source: AudioBufferSourceNode | null = null;
  private gainLocal: GainNode | null = null;
  private encours: string | null = null;
  /** `currentTime` du contexte au moment où la lecture a commencé. */
  private departCtx = 0;
  private taux = 1;
  private dureeBufferMs = 0;
  private boucle = false;
  private volumeMorceau = 1;
  /** ⟦C50⟧ La latence de sortie dont le départ de la lecture en cours a été avancé, en ms. */
  private compensationMs = 0;
  /** Incrémenté à chaque `jouer`/`arreter` : une lecture périmée ne prend jamais la main. */
  private jeton = 0;

  constructor(
    private readonly reveiller: () => AudioContext | null,
    private readonly sortie: () => GainNode | null,
    private readonly reglages: () => Reglages,
    private readonly manifeste: () => ManifesteMusique | null,
  ) {}

  /** L'id du morceau en cours, ou `null`. */
  idEnCours(): string | null {
    return this.encours;
  }

  /** La grille du morceau en cours — `null` si absente, illisible, ou pas encore chargée. */
  grilleEnCours(): Beatmap | null {
    return this.encours ? (this.grilles.get(this.encours) ?? null) : null;
  }

  /**
   * Position de lecture en ms depuis le début du morceau, ou `null` si rien ne joue.
   *
   * En boucle, la valeur revient à zéro à chaque tour — c'est bien la position *dans le
   * morceau*, celle qu'une beatmap sait interpréter.
   */
  positionMs(): number | null {
    const ctx = this.contexteVivant();
    if (!ctx || !this.source) return null;
    const ecouleMs = (ctx.currentTime - this.departCtx) * 1000 * this.taux;
    if (ecouleMs < 0) return 0;
    if (this.boucle && this.dureeBufferMs > 0) return ecouleMs % this.dureeBufferMs;
    if (this.dureeBufferMs > 0 && ecouleMs > this.dureeBufferMs) return null;
    return ecouleMs;
  }

  /**
   * ⟦C50⟧ La position ENTENDUE, en ms depuis le début du fichier : celle de `positionMs()` moins la latence de
   * sortie dont on a avancé le départ. C'est celle qu'on compare à l'horloge du jeu (la sonde de dérive), et
   * elle vaut `origineMs + t` quand tout est juste. `null` aux mêmes conditions que `positionMs()`.
   */
  positionEntendueMs(): number | null {
    const p = this.positionMs();
    return p === null ? null : p - this.compensationMs * this.taux;
  }

  /** ⟦C50⟧ La latence de sortie de la carte son, MAINTENANT (0 sans contexte). Voir `latence.ts`. */
  mesureLatence(): MesureLatence {
    const ctx = this.contexteVivant();
    return ctx ? mesurerLatenceSortie(ctx) : { ms: 0, source: "aucune" };
  }

  /** ⟦C50⟧ La latence de sortie dont la lecture EN COURS a été avancée, en ms. */
  compensationEnCoursMs(): number {
    return this.compensationMs;
  }

  private contexteVivant(): AudioContext | null {
    const ctx = this.reveiller();
    return ctx && ctx.state !== "closed" ? ctx : null;
  }

  /**
   * Lance un morceau. **Ne rend rien et ne lève rien** : voir l'en-tête, garantie n° 2.
   *
   * Un appel pendant qu'un autre morceau joue arrête le précédent. Rejouer l'id déjà en cours
   * ne le redémarre pas — sans quoi un `useEffect` mal mémoïsé ferait bégayer la musique à
   * chaque rendu, ce qui est le bug le plus courant de cette famille de code.
   */
  jouer(id: string, options: OptionsLecture = {}): void {
    if (this.encours === id && this.source) return;
    const { mute } = this.reglages();
    if (mute) return; // garantie n° 1 : aucun écran n'est au-dessus du réglage du joueur.

    const entree = this.manifeste()?.[id];
    if (!entree) return; // id absent ou mappé à `null` : silence, comme pour les SFX (§7.6).

    const ctx = this.contexteVivant();
    const maitre = this.sortie();
    if (!ctx || !maitre) return;

    this.arreter();
    const monJeton = ++this.jeton;
    this.encours = id;
    this.boucle = options.boucle === true;
    this.taux = options.playbackRate ?? 1;
    this.volumeMorceau = options.volume ?? 1;
    /** L'heure de l'appel : le calage se mesure depuis elle, pas depuis la fin du décodage. */
    const appelCtx = ctx.currentTime;

    void this.charger(id, entree, ctx).then((buffer) => {
      // La lecture a-t-elle été annulée pendant le chargement ? (écran quitté, autre morceau)
      if (!buffer || monJeton !== this.jeton) return;
      if (this.reglages().mute) return; // le joueur a coupé le son PENDANT le décodage.
      const maintenant = this.contexteVivant();
      const versMaitre = this.sortie();
      if (!maintenant || !versMaitre) return;

      const source = maintenant.createBufferSource();
      source.buffer = buffer;
      source.loop = this.boucle;
      source.playbackRate.value = this.taux;

      // ⟦C50⟧ Le calage : voir `caler`. `quand` est l'heure carte-son du départ, `dansLeMorceauS` la position
      // de départ DANS le buffer (en secondes de fichier).
      let quand = maintenant.currentTime;
      let dansLeMorceauS = 0;
      this.compensationMs = 0;
      if (options.caler !== undefined) {
        const latenceMs = options.compenserLatence === false ? 0 : mesurerLatenceSortie(maintenant).ms;
        this.compensationMs = latenceMs;
        // L'instant (depuis l'appel) où le DÉBUT du fichier doit être ENTENDU pour que `positionMs` le soit à
        // `dansMs` — l'étirement raccourcit ou allonge ce qui précède, d'où la division.
        const departEntenduMs = options.caler.dansMs - options.caler.positionMs / this.taux;
        // Le moteur doit le RENDRE `latenceMs` plus tôt : c'est tout le correctif.
        const resteMs = departEntenduMs - latenceMs - (maintenant.currentTime - appelCtx) * 1000;
        if (resteMs >= 0) {
          quand = maintenant.currentTime + resteMs / 1000;
        } else {
          dansLeMorceauS = (-resteMs / 1000) * this.taux;
          if (dansLeMorceauS >= buffer.duration) return; // le calage est déjà passé : silence.
        }
      }

      const local = maintenant.createGain();
      const cible = this.volumeCible();
      const fondue = (options.fondueMs ?? 0) / 1000;
      if (fondue > 0) {
        // Le fondu part de `quand`, pas de l'heure de l'appel : sur un départ programmé, une
        // rampe déjà finie serait un démarrage sec déguisé.
        local.gain.setValueAtTime(0.0001, quand);
        local.gain.exponentialRampToValueAtTime(Math.max(0.0001, cible), quand + fondue);
      } else {
        local.gain.value = cible;
      }

      source.connect(local).connect(versMaitre);
      source.start(quand, dansLeMorceauS);
      this.source = source;
      this.gainLocal = local;
      // `positionMs()` doit rester vraie quel que soit le départ : l'origine recule d'autant.
      this.departCtx = quand - dansLeMorceauS / this.taux;
      this.dureeBufferMs = buffer.duration * 1000;
      source.onended = () => {
        // `onended` tombe aussi quand c'est NOUS qui coupons : on ne nettoie que si la source
        // qui se termine est bien celle qu'on croit encore en train de jouer.
        if (this.source === source) {
          this.source = null;
          this.gainLocal = null;
          this.encours = null;
        }
      };
    });
  }

  private volumeCible(): number {
    const { mute, volume } = this.reglages();
    if (mute) return 0;
    return Math.max(0, Math.min(1, volume * this.volumeMorceau));
  }

  /** Recalcule le gain quand les réglages changent en cours de lecture. */
  majReglages(): void {
    const ctx = this.contexteVivant();
    if (!ctx || !this.gainLocal) return;
    if (this.reglages().mute) {
      // Couper le son doit couper le son MAINTENANT, pas au prochain morceau.
      this.arreter({ fondueMs: 80 });
      return;
    }
    this.gainLocal.gain.setTargetAtTime(this.volumeCible(), ctx.currentTime, 0.02);
  }

  /**
   * Arrête le morceau en cours. Sans fondu par défaut — un écran qu'on quitte ne doit pas
   * traîner une queue de musique sur le suivant.
   */
  arreter(options: { fondueMs?: number } = {}): void {
    this.jeton++;
    const source = this.source;
    const local = this.gainLocal;
    this.source = null;
    this.gainLocal = null;
    this.encours = null;
    if (!source) return;
    const ctx = this.contexteVivant();
    const fondue = (options.fondueMs ?? 0) / 1000;
    if (ctx && local && fondue > 0) {
      local.gain.setTargetAtTime(0.0001, ctx.currentTime, fondue / 3);
      try {
        source.stop(ctx.currentTime + fondue);
      } catch {
        /* déjà arrêtée */
      }
      return;
    }
    try {
      source.stop();
    } catch {
      /* déjà arrêtée : `stop()` deux fois lève, et ce n'est pas une panne */
    }
  }

  /**
   * ⟦A-76⟧ Les pistes du manifeste dont l'id commence par `prefixe` et qui portent un BPM —
   * la forme exacte que `choisirPiste` attend. Vide tant que le manifeste n'est pas chargé,
   * et l'appelant doit traiter ce vide comme du silence, pas comme une erreur.
   */
  pistesDisponibles(prefixe: string): PisteDisponible[] {
    const manifeste = this.manifeste();
    if (!manifeste) return [];
    const pistes: PisteDisponible[] = [];
    for (const [id, entree] of Object.entries(manifeste)) {
      if (!id.startsWith(prefixe)) continue;
      if (!entree || typeof entree.bpm !== "number" || !(entree.bpm > 0)) continue;
      pistes.push({ id, bpm: entree.bpm });
    }
    return pistes;
  }

  /**
   * ⟦A-76⟧ Décode un morceau en avance, sans le jouer. Le Montage l'appelle à l'intro pour que
   * le calage au passage en jeu ne paie pas le décodage. Suppose qu'un geste utilisateur a déjà
   * eu lieu (c'est le cas : on arrive à l'Atelier en cliquant) — sinon no-op silencieux.
   */
  precharger(id: string): void {
    const entree = this.manifeste()?.[id];
    const ctx = this.contexteVivant();
    if (!entree || !ctx) return;
    void this.charger(id, entree, ctx);
  }

  /** Charge (et met en cache) le buffer et la grille d'un morceau. */
  private async charger(
    id: string,
    entree: EntreeMusique,
    ctx: AudioContext,
  ): Promise<AudioBuffer | null> {
    if (!this.grilles.has(id)) {
      // La grille est facultative : son absence n'empêche pas d'écouter, elle empêche seulement
      // de se caler dessus.
      let grille: Beatmap | null = null;
      if (entree.beatmap) {
        try {
          const r = await fetch(entree.beatmap);
          if (r.ok) grille = lireBeatmap(await r.json());
        } catch {
          grille = null;
        }
      }
      this.grilles.set(id, grille);
    }

    const dejaLa = this.buffers.get(id);
    if (dejaLa) return dejaLa;
    try {
      const r = await fetch(entree.file);
      if (!r.ok) return null;
      const buffer = await ctx.decodeAudioData(await r.arrayBuffer());
      this.buffers.set(id, buffer);
      return buffer;
    } catch {
      return null;
    }
  }
}
